/**
 * store-locator.js — Shopify OS2.0 Store Locator
 * ─────────────────────────────────────────────────────────────────
 * Architecture:
 *   - StoreLocatorCSV      CSV parsing + validation
 *   - GoogleMapsProvider   Google Maps integration + clustering
 *   - MapboxProvider       Mapbox GL JS integration + clustering
 *   - StoreLocator         Main orchestrator class
 *
 * No external runtime dependencies (map SDKs are lazy-loaded).
 * Entry: window.StoreLocator is instantiated by the Liquid section.
 * ─────────────────────────────────────────────────────────────────
 */

/* ================================================================
   1. CSV PARSER + VALIDATOR
   ================================================================ */

/**
 * Parses a CSV string into an array of normalised store objects.
 * Handles quoted fields, Windows (\r\n) and Unix (\n) line endings.
 */
const StoreLocatorCSV = (() => {

  /**
   * Required columns every row must contain (non-empty).
   * lat/lng are required for map plotting; rows without them
   * are collected into a `warnings` array but still returned.
   */
  const REQUIRED_COLUMNS = ['name', 'address', 'city', 'state', 'country', 'zip'];
  const LAT_LNG_COLUMNS  = ['latitude', 'longitude'];
  const OPTIONAL_COLUMNS = ['phone', 'website', 'store_type', 'notes'];
  const ALL_COLUMNS       = [...REQUIRED_COLUMNS, ...LAT_LNG_COLUMNS, ...OPTIONAL_COLUMNS];

  /**
   * Tokenise a single CSV line, handling quoted fields that may
   * contain commas or newlines.
   * @param {string} line
   * @returns {string[]}
   */
  function parseLine(line) {
    const result = [];
    let cur       = '';
    let inQuotes  = false;

    for (let i = 0; i < line.length; i++) {
      const ch   = line[i];
      const next = line[i + 1];

      if (inQuotes) {
        if (ch === '"' && next === '"') { cur += '"'; i++; }      // escaped quote
        else if (ch === '"')             { inQuotes = false; }    // closing quote
        else                             { cur += ch; }
      } else {
        if (ch === '"')  { inQuotes = true; }
        else if (ch === ',') { result.push(cur.trim()); cur = ''; }
        else               { cur += ch; }
      }
    }
    result.push(cur.trim());
    return result;
  }

  /**
   * Parse full CSV text into an array of store objects.
   * @param {string} csvText
   * @returns {{ stores: Object[], warnings: string[], errors: string[] }}
   */
  function parse(csvText) {
    const stores   = [];
    const warnings = [];
    const errors   = [];

    // Normalise line endings
    const lines = csvText.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n');

    if (lines.length < 2) {
      errors.push('CSV file appears to be empty or has no data rows.');
      return { stores, warnings, errors };
    }

    // ── Header row ──────────────────────────────────────────
    const headers = parseLine(lines[0]).map(h => h.toLowerCase().trim().replace(/\s+/g, '_'));

    // Validate that all required columns exist in the header
    const missingRequired = REQUIRED_COLUMNS.filter(col => !headers.includes(col));
    if (missingRequired.length > 0) {
      errors.push(`CSV is missing required columns: ${missingRequired.join(', ')}`);
      return { stores, warnings, errors };
    }

    const missingLatLng = LAT_LNG_COLUMNS.filter(col => !headers.includes(col));
    if (missingLatLng.length > 0) {
      warnings.push(`CSV is missing columns: ${missingLatLng.join(', ')}. Stores without coordinates will not appear on the map.`);
    }

    // ── Data rows ────────────────────────────────────────────
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue; // skip blank rows

      const values = parseLine(line);
      if (values.length !== headers.length) {
        warnings.push(`Row ${i + 1} has ${values.length} columns (expected ${headers.length}). Skipping.`);
        continue;
      }

      // Build raw store object
      const raw = {};
      headers.forEach((header, idx) => { raw[header] = values[idx] || ''; });

      // Validate required fields are non-empty
      const emptyRequired = REQUIRED_COLUMNS.filter(col => !raw[col]);
      if (emptyRequired.length > 0) {
        warnings.push(`Row ${i + 1} ("${raw.name || 'unnamed'}"): missing required values for ${emptyRequired.join(', ')}. Skipping.`);
        continue;
      }

      // Parse lat/lng as floats
      const lat = parseFloat(raw.latitude);
      const lng = parseFloat(raw.longitude);
      const hasCoords = !isNaN(lat) && !isNaN(lng)
                        && lat >= -90  && lat <= 90
                        && lng >= -180 && lng <= 180;

      if (!hasCoords) {
        warnings.push(`Row ${i + 1} ("${raw.name}"): invalid or missing latitude/longitude. Store will appear in list only.`);
      }

      // Normalise website URL
      let website = raw.website || '';
      if (website && !website.match(/^https?:\/\//i)) {
        website = 'https://' + website;
      }

      // Build clean store object
      const store = {
        id:         i, // stable integer index used as marker key
        name:       raw.name,
        address:    raw.address,
        city:       raw.city,
        state:      raw.state,
        country:    raw.country,
        zip:        raw.zip,
        lat:        hasCoords ? lat : null,
        lng:        hasCoords ? lng : null,
        phone:      raw.phone || '',
        website,
        store_type: raw.store_type || '',
        notes:      raw.notes || '',
        hasCoords,
        // Derived display fields
        fullAddress: [raw.address, raw.city, raw.state, raw.zip, raw.country]
                       .filter(Boolean).join(', '),
        searchIndex: [raw.name, raw.city, raw.zip, raw.country, raw.state, raw.store_type]
                       .join(' ').toLowerCase(),
      };

      stores.push(store);
    }

    if (stores.length === 0 && errors.length === 0) {
      warnings.push('No valid store rows found in the CSV file.');
    }

    return { stores, warnings, errors };
  }

  return { parse };
})();


/* ================================================================
   2. GOOGLE MAPS PROVIDER
   ================================================================ */

class GoogleMapsProvider {
  /**
   * @param {HTMLElement} mapEl  — DOM element to mount map into
   * @param {Object}      config — { apiKey, defaultZoom }
   */
  constructor(mapEl, config) {
    this.mapEl      = mapEl;
    this.config     = config;
    this.map        = null;
    this.infoWindow = null;
    this.markers    = new Map(); // storeId → google.maps.Marker
    this.clusterer  = null;
    this._onMarkerClick = null; // callback(store)
    this._userMarker    = null; // red dot for user location
    this._userLatLng    = null; // { lat, lng } of user
  }

  /** Load the Maps JS API + MarkerClusterer, then initialise the map. */
  async load() {
    await this._loadScript(
      `https://maps.googleapis.com/maps/api/js?key=${this.config.apiKey}&loading=async`,
      'google-maps-api'
    );
    // MarkerClusterer (official @googlemaps/markerclusterer UMD build via CDN)
    await this._loadScript(
      'https://unpkg.com/@googlemaps/markerclusterer/dist/index.min.js',
      'google-maps-clusterer'
    );
    this._init();
  }

  /** Initialise map instance + info window. */
  _init() {
    this.map = new google.maps.Map(this.mapEl, {
      zoom:              this.config.defaultZoom || 5,
      center:            { lat: 20, lng: 0 },  // world default; overridden later
      mapTypeControl:    false,
      fullscreenControl: true,
      streetViewControl: false,
      gestureHandling:   'cooperative',
      styles: [
        // Subtle grey-scale style that works on both light/dark UIs
        { featureType: 'poi',   elementType: 'labels',       stylers: [{ visibility: 'off' }] },
        { featureType: 'transit', elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
      ],
    });

    this.infoWindow = new google.maps.InfoWindow({ maxWidth: 300, disableAutoPan: false });

    // Hide Google's native close button — we render our own inside the content
    google.maps.event.addListener(this.infoWindow, 'domready', () => {
      const nativeClose = document.querySelector('.gm-ui-hover-effect');
      if (nativeClose) nativeClose.style.display = 'none';

      // Wire up our custom close button
      const btn = document.querySelector('.sl-info-window__close');
      if (btn) btn.addEventListener('click', () => this.infoWindow.close());
    });
  }

  /**
   * Plot all stores with valid coordinates as markers.
   * Clears existing markers + clusterer first.
   * @param {Object[]}  stores
   * @param {Function}  onMarkerClick  — called with (store) when a marker is clicked
   */
  plotMarkers(stores, onMarkerClick) {
    this._onMarkerClick = onMarkerClick;

    // Clear existing
    this.clearMarkers();

    const newMarkers = [];

    stores.forEach(store => {
      if (!store.hasCoords) return;

      // Blue map-pin SVG encoded as a data URL
      const pinSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="42" viewBox="0 0 32 42"><path fill="#1a73e8" stroke="#fff" stroke-width="1.5" d="M16 2C9.373 2 4 7.373 4 14c0 9.625 12 26 12 26s12-16.375 12-26c0-6.627-5.373-12-12-12z"/><circle cx="16" cy="14" r="5" fill="#fff"/></svg>`;
      const pinUrl  = 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(pinSvg);

      const marker = new google.maps.Marker({
        position: { lat: store.lat, lng: store.lng },
        map:      null, // managed by clusterer
        title:    store.name,
        icon: {
          url:    pinUrl,
          size:   new google.maps.Size(32, 42),
          origin: new google.maps.Point(0, 0),
          anchor: new google.maps.Point(16, 42),
        },
      });

      marker.addListener('click', () => {
        const distHtml = this._userLatLng
          ? this._buildDistanceHtml(store, this._userLatLng.lat, this._userLatLng.lng)
          : '';
        this._showInfoWindow(marker, store, distHtml);
        if (this._onMarkerClick) this._onMarkerClick(store);
      });

      this.markers.set(store.id, marker);
      newMarkers.push(marker);
    });

    // Cluster the markers
    if (window.markerClusterer && newMarkers.length > 0) {
      this.clusterer = new markerClusterer.MarkerClusterer({
        map:     this.map,
        markers: newMarkers,
        renderer: {
          render({ count, position }) {
            return new google.maps.Marker({
              position,
              label: {
                text:     String(count),
                color:    '#ffffff',
                fontSize: '1.3rem',
                fontWeight: '700',
              },
              icon: {
                path:        google.maps.SymbolPath.CIRCLE,
                scale:       22,
                fillColor:   '#111111',
                fillOpacity: 0.9,
                strokeColor: '#ffffff',
                strokeWeight: 2,
              },
              zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
            });
          },
        },
      });
    } else {
      // Fallback: add markers directly to map without clustering
      newMarkers.forEach(m => m.setMap(this.map));
    }

    // Fit map to visible markers
    if (newMarkers.length > 0) {
      this.fitBounds(stores.filter(s => s.hasCoords));
    }
  }

  /** Show info-window for a given store above its marker. */
  _showInfoWindow(marker, store, distanceHtml = '') {
    this.infoWindow.setContent(this._buildInfoContent(store, distanceHtml));
    this.infoWindow.open({ anchor: marker, map: this.map });
  }

  /** Build a small distance line for the info window. */
  _buildDistanceHtml(store, userLat, userLng) {
    if (!store.hasCoords) return '';
    const R    = 6371;
    const dLat = (store.lat - userLat) * Math.PI / 180;
    const dLng = (store.lng - userLng) * Math.PI / 180;
    const a    = Math.sin(dLat/2)**2 + Math.cos(userLat*Math.PI/180)*Math.cos(store.lat*Math.PI/180)*Math.sin(dLng/2)**2;
    const km   = R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    const mi   = km * 0.621371;
    return `<p style="margin:0.5rem 0 0;font-size:1.2rem;color:#444;font-weight:500;">${mi.toFixed(1)} mi (${km.toFixed(1)} km) away</p>`;
  }

  /** Place (or move) the red user-location dot on the map. */
  setUserLocation(lat, lng) {
    this._userLatLng = { lat, lng };
    if (this._userMarker) {
      this._userMarker.setPosition({ lat, lng });
    } else {
      this._userMarker = new google.maps.Marker({
        position: { lat, lng },
        map:      this.map,
        title:    'Your location',
        zIndex:   Number(google.maps.Marker.MAX_ZINDEX) + 9999,
        icon: {
          path:        google.maps.SymbolPath.CIRCLE,
          scale:       9,
          fillColor:   '#e63228',
          fillOpacity: 1,
          strokeColor: '#ffffff',
          strokeWeight: 2,
        },
      });
    }
  }

  /** Highlight (bounce) the marker for a given storeId. */
  highlightMarker(storeId) {
    this.markers.forEach((marker, id) => {
      if (id === storeId) {
        marker.setAnimation(google.maps.Animation.BOUNCE);
        setTimeout(() => marker.setAnimation(null), 1400);
        this.map.panTo(marker.getPosition());
        this.map.setZoom(Math.max(this.map.getZoom(), 10));
        // Trigger the info window
        this._showInfoWindow(marker, null); // content set below
      }
    });
  }

  /** Open info window on the marker for a given store object. */
  openInfoForStore(store) {
    const marker = this.markers.get(store.id);
    if (!marker) return;
    const distHtml = this._userLatLng
      ? this._buildDistanceHtml(store, this._userLatLng.lat, this._userLatLng.lng)
      : '';
    this._showInfoWindow(marker, store, distHtml);
    this.map.panTo(marker.getPosition());
    this.map.setZoom(Math.max(this.map.getZoom(), 10));
  }

  /** Fit the map viewport to encompass all provided stores. */
  fitBounds(stores) {
    const valid = stores.filter(s => s.hasCoords);
    if (valid.length === 0) return;

    // Single store: don't zoom to max — show ~5–10 mile context around the pin
    if (valid.length === 1) {
      const { lat, lng } = valid[0];
      this.map.panTo({ lat, lng });
      this.map.setZoom(13);
      return;
    }

    const bounds = new google.maps.LatLngBounds();
    valid.forEach(s => bounds.extend({ lat: s.lat, lng: s.lng }));
    this.map.fitBounds(bounds, { top: 40, right: 40, bottom: 40, left: 40 });

    // Clamp max zoom so tightly-clustered stores don't over-zoom
    google.maps.event.addListenerOnce(this.map, 'idle', () => {
      if (this.map.getZoom() > 14) this.map.setZoom(14);
    });
  }

  /** Pan/zoom to a specific lat/lng. */
  panTo(lat, lng, zoom) {
    this.map.panTo({ lat, lng });
    if (zoom) this.map.setZoom(zoom);
  }

  /** Remove all store markers and destroy clusterer. Does NOT remove user location dot. */
  clearMarkers() {
    if (this.clusterer) {
      this.clusterer.clearMarkers();
      this.clusterer = null;
    }
    this.markers.forEach(m => m.setMap(null));
    this.markers.clear();
    if (this.infoWindow) this.infoWindow.close();
  }

  /** Build the HTML string used inside the Google Maps InfoWindow. */
  _buildInfoContent(store, distanceHtml = '') {
    if (!store) return '';
    const badgeClass = this._badgeClass(store.store_type);
    const phone      = store.phone
      ? `<a href="tel:${store.phone}" style="color:#1a6fc4;text-decoration:none;font-size:1.3rem;font-weight:500;">${store.phone}</a>` : '';
    const website    = store.website
      ? `<a href="${store.website}" target="_blank" rel="noopener noreferrer" style="color:#1a6fc4;text-decoration:none;font-size:1.3rem;font-weight:500;">${store.website.replace(/^https?:\/\//, '')}</a>` : '';
    const badge      = store.store_type
      ? `<span class="sl-info-window__badge" style="background:${badgeClass.bg};color:${badgeClass.color}">${store.store_type}</span>` : '';

    return `
      <div class="sl-info-window">
        <button class="sl-info-window__close" type="button" aria-label="Close">&#x2715;</button>
        <p class="sl-info-window__name" style="color:#111;font-weight:700;">${this._esc(store.name)}</p>
        <p class="sl-info-window__address" style="color:#333;font-weight:500;">${this._esc(store.fullAddress)}</p>
        ${phone || website ? `<div class="sl-info-window__meta">${phone}${website}</div>` : ''}
        ${badge}
        ${distanceHtml}
      </div>`;
  }

  _badgeClass(type) {
    const t = (type || '').toLowerCase();
    if (t.includes('shop'))        return { bg: 'rgba(39,174,96,0.12)',  color: 'rgb(27,120,64)' };
    if (t.includes('distributor')) return { bg: 'rgba(41,98,255,0.12)',  color: 'rgb(30,80,200)' };
    if (t.includes('demo'))        return { bg: 'rgba(230,50,40,0.12)',  color: 'rgb(180,35,25)' };
    return { bg: '#eee', color: '#555' };
  }

  _esc(str) {
    return String(str || '')
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  /** Dynamically inject a `<script>` tag once; resolves when loaded. */
  _loadScript(src, id) {
    return new Promise((resolve, reject) => {
      if (document.getElementById(id)) { resolve(); return; }
      const s = document.createElement('script');
      s.id    = id;
      s.src   = src;
      s.async = true;
      s.defer = true;
      s.onload  = resolve;
      s.onerror = () => reject(new Error(`Failed to load script: ${src}`));
      document.head.appendChild(s);
    });
  }
}


/* ================================================================
   3. MAPBOX PROVIDER
   ================================================================ */

class MapboxProvider {
  /**
   * @param {HTMLElement} mapEl
   * @param {Object}      config — { apiKey (token), defaultZoom }
   */
  constructor(mapEl, config) {
    this.mapEl        = mapEl;
    this.config       = config;
    this.map          = null;
    this.popup        = null;
    this._stores      = [];
    this._onMarkerClick = null;
  }

  /** Load Mapbox GL JS + CSS, then initialise the map. */
  async load() {
    await this._loadStylesheet(
      'https://api.mapbox.com/mapbox-gl-js/v3.3.0/mapbox-gl.css',
      'mapbox-gl-css'
    );
    await this._loadScript(
      'https://api.mapbox.com/mapbox-gl-js/v3.3.0/mapbox-gl.js',
      'mapbox-gl-js'
    );
    this._init();
  }

  _init() {
    mapboxgl.accessToken = this.config.apiKey;
    this.map = new mapboxgl.Map({
      container: this.mapEl,
      style:     'mapbox://styles/mapbox/light-v11',
      center:    [0, 20],
      zoom:      this.config.defaultZoom || 2,
    });
    this.map.addControl(new mapboxgl.NavigationControl(), 'top-right');
    this.map.addControl(new mapboxgl.FullscreenControl());
    this.popup = new mapboxgl.Popup({ closeButton: true, maxWidth: '300px' });
  }

  /** Add a GeoJSON source with built-in clustering + layers. */
  plotMarkers(stores, onMarkerClick) {
    this._stores        = stores;
    this._onMarkerClick = onMarkerClick;

    const geojson = {
      type: 'FeatureCollection',
      features: stores
        .filter(s => s.hasCoords)
        .map(s => ({
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [s.lng, s.lat] },
          properties: { id: s.id, name: s.name, store_type: s.store_type },
        })),
    };

    const addLayers = () => {
      if (this.map.getSource('stores')) {
        this.map.getSource('stores').setData(geojson);
        return;
      }

      this.map.addSource('stores', {
        type: 'geojson',
        data: geojson,
        cluster: true,
        clusterMaxZoom: 14,
        clusterRadius: 50,
      });

      // Cluster circle
      this.map.addLayer({
        id: 'clusters',
        type: 'circle',
        source: 'stores',
        filter: ['has', 'point_count'],
        paint: {
          'circle-color': '#111111',
          'circle-radius': ['step', ['get', 'point_count'], 18, 10, 22, 30, 26],
          'circle-opacity': 0.9,
          'circle-stroke-width': 2,
          'circle-stroke-color': '#ffffff',
        },
      });

      // Cluster count label
      this.map.addLayer({
        id: 'cluster-count',
        type: 'symbol',
        source: 'stores',
        filter: ['has', 'point_count'],
        layout: {
          'text-field': ['get', 'point_count_abbreviated'],
          'text-font':  ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
          'text-size':  13,
        },
        paint: { 'text-color': '#ffffff' },
      });

      // Individual store pin
      this.map.addLayer({
        id: 'unclustered-point',
        type: 'circle',
        source: 'stores',
        filter: ['!', ['has', 'point_count']],
        paint: {
          'circle-color': '#e63228',
          'circle-radius': 9,
          'circle-stroke-width': 2,
          'circle-stroke-color': '#ffffff',
        },
      });

      // Expand cluster on click
      this.map.on('click', 'clusters', (e) => {
        const features = this.map.queryRenderedFeatures(e.point, { layers: ['clusters'] });
        const clusterId = features[0].properties.cluster_id;
        this.map.getSource('stores').getClusterExpansionZoom(clusterId, (err, zoom) => {
          if (err) return;
          this.map.easeTo({ center: features[0].geometry.coordinates, zoom });
        });
        this.map.getCanvas().style.cursor = '';
      });

      // Individual marker click → show popup + fire callback
      this.map.on('click', 'unclustered-point', (e) => {
        const props = e.features[0].properties;
        const store  = this._stores.find(s => s.id === props.id);
        if (!store) return;
        this._showPopup(e.features[0].geometry.coordinates, store);
        if (this._onMarkerClick) this._onMarkerClick(store);
      });

      // Cursor changes
      ['clusters', 'unclustered-point'].forEach(layer => {
        this.map.on('mouseenter', layer, () => { this.map.getCanvas().style.cursor = 'pointer'; });
        this.map.on('mouseleave', layer, () => { this.map.getCanvas().style.cursor = '';        });
      });
    };

    if (this.map.isStyleLoaded()) {
      addLayers();
    } else {
      this.map.once('load', addLayers);
    }

    // Fit bounds
    const coords = stores.filter(s => s.hasCoords).map(s => [s.lng, s.lat]);
    if (coords.length > 0) this._fitBoundsToCoords(coords);
  }

  _showPopup(coords, store) {
    const phone   = store.phone   ? `<a href="tel:${store.phone}">${store.phone}</a>`   : '';
    const website = store.website ? `<a href="${store.website}" target="_blank" rel="noopener noreferrer">${store.website.replace(/^https?:\/\//, '')}</a>` : '';

    this.popup
      .setLngLat(coords)
      .setHTML(`
        <div class="sl-info-window">
          <p class="sl-info-window__name">${store.name}</p>
          <p class="sl-info-window__address">${store.fullAddress}</p>
          ${phone || website ? `<div class="sl-info-window__meta">${phone}${website}</div>` : ''}
          ${store.store_type ? `<span class="sl-info-window__badge">${store.store_type}</span>` : ''}
        </div>`)
      .addTo(this.map);
  }

  openInfoForStore(store) {
    if (!store.hasCoords) return;
    this._showPopup([store.lng, store.lat], store);
    this.map.flyTo({ center: [store.lng, store.lat], zoom: Math.max(this.map.getZoom(), 10) });
  }

  highlightMarker(storeId) {
    const store = this._stores.find(s => s.id === storeId);
    if (store) this.openInfoForStore(store);
  }

  fitBounds(stores) {
    const coords = stores.filter(s => s.hasCoords).map(s => [s.lng, s.lat]);
    this._fitBoundsToCoords(coords);
  }

  panTo(lat, lng, zoom) {
    this.map.flyTo({ center: [lng, lat], zoom: zoom || 10 });
  }

  clearMarkers() {
    if (!this.map) return;
    ['unclustered-point', 'cluster-count', 'clusters'].forEach(layer => {
      if (this.map.getLayer(layer)) this.map.removeLayer(layer);
    });
    if (this.map.getSource('stores')) this.map.removeSource('stores');
    if (this.popup) this.popup.remove();
  }

  _fitBoundsToCoords(coords) {
    if (coords.length === 0) return;
    if (coords.length === 1) { this.map.flyTo({ center: coords[0], zoom: 12 }); return; }

    const bounds = coords.reduce(
      (b, c) => [[Math.min(b[0][0], c[0]), Math.min(b[0][1], c[1])],
                 [Math.max(b[1][0], c[0]), Math.max(b[1][1], c[1])]],
      [[coords[0][0], coords[0][1]], [coords[0][0], coords[0][1]]]
    );
    this.map.fitBounds(bounds, { padding: 60, maxZoom: 14 });
  }

  _loadScript(src, id) {
    return new Promise((resolve, reject) => {
      if (document.getElementById(id)) { resolve(); return; }
      const s = document.createElement('script');
      s.id = id; s.src = src; s.async = true; s.defer = true;
      s.onload = resolve; s.onerror = () => reject(new Error(`Failed to load: ${src}`));
      document.head.appendChild(s);
    });
  }

  _loadStylesheet(href, id) {
    return new Promise(resolve => {
      if (document.getElementById(id)) { resolve(); return; }
      const link = document.createElement('link');
      link.id = id; link.rel = 'stylesheet'; link.href = href;
      link.onload = resolve;
      document.head.appendChild(link);
    });
  }
}


/* ================================================================
   4. MAIN STORE LOCATOR CLASS
   ================================================================ */

class StoreLocator {
  /**
   * @param {Object} config
   * @param {string}  config.sectionId        — Unique Liquid section.id
   * @param {string}  config.csvUrl           — asset_url of the CSV file
   * @param {string}  config.mapProvider      — 'google' | 'mapbox'
   * @param {string}  config.apiKey           — Maps API key / Mapbox token
   * @param {number}  config.defaultZoom      — default map zoom level
   * @param {boolean} config.enableGeolocation
   */
  constructor(config) {
    this.config          = config;
    this.allStores       = [];
    this.filteredStores  = [];
    this.activeFilter    = 'all';
    this.searchQuery     = '';
    this.mapProvider     = null;
    this.mapReady        = false;
    this._searchDebounce = null;
    this._userLatLng     = null; // persisted so distances survive list re-renders
    this._distanceLabel  = 'your location'; // label for distance display (e.g., "your location", "New York", "10001")
    // Bind DOM refs after DOMContentLoaded guarantees section is rendered
    this._bindDom();
    this.init();
  }

  async init() {
    try {
      // ── 1. Fetch + parse CSV ───────────────────────────────
      const { stores, warnings, errors } = await this._loadStores();

      if (errors.length > 0) {
        this._showNotice('error', errors[0]);
        this._setListCount(0, true);
        this._hideMapLoader();
        return;
      }

      if (warnings.length > 0) {
        this._showNotice('warning', warnings[0]);
      }

      this.allStores      = stores;
      this.filteredStores = [...stores];

      // ── 2. Build dynamic filter chips from store_type values ─
      this._buildFilterChips(stores);

      // ── 3. Render initial list ──────────────────────────────
      this._renderList(stores);
      this._setListCount(stores.length);

      // ── 4. Bind UI events ──────────────────────────────────
      this._bindEvents();

      // ── 5. Lazy-load & initialise map ──────────────────────
      await this._initMap();

      // ── 6. Auto-geocode any rows missing lat/lng ───────────
      // Store owners can leave latitude/longitude blank — we resolve them
      // from the address via the map provider's geocoder and cache the
      // result in localStorage so subsequent loads are free.
      await this._geocodeMissing();

      // ── 7. Auto-geolocation ────────────────────────────────
      if (this.config.enableGeolocation) {
        this._detectLocation();
      }

    } catch (err) {
      console.error('[StoreLocator]', err);
      this._showNotice('error', `Store locator failed to load: ${err.message}`);
      this._setListCount(0, true);
      this._hideMapLoader();
    }
  }

  /* ── DOM binding ──────────────────────────────────────── */

  /** Query and store all relevant DOM elements. */
  _bindDom() {
    const sid  = this.config.sectionId;
    const root = document.getElementById(`store-locator-${sid}`);
    if (!root) { console.error('[StoreLocator] Root element not found.'); return; }

    this.dom = {
      root,
      searchInput:  root.querySelector('.store-locator__search-input'),
      searchClear:  root.querySelector('.store-locator__search-clear'),
      filtersBar:   root.querySelector('.store-locator__filters'),
      geolocateBtn: root.querySelector('.store-locator__geolocate-btn'),
      listEl:       root.querySelector('.store-locator__list'),
      listCount:    root.querySelector('.store-locator__list-count'),
      mapEl:        root.querySelector('.store-locator__map'),
      mapLoader:    root.querySelector('.store-locator__map-loader'),
      emptyState:   root.querySelector('.store-locator__empty'),
      noticeBanner: root.querySelector('.store-locator__notice'),
      noticeText:   root.querySelector('.store-locator__notice-text'),
      body:         root.querySelector('.store-locator__body'),
      mobileTabs:   root.querySelectorAll('.store-locator__mobile-tab'),
    };
  }

  /* ── Data loading ─────────────────────────────────────── */

  async _loadStores() {
    const url = this.config.csvUrl;
    if (!url) throw new Error('No CSV file configured. Please set the "CSV Filename" in section settings.');

    const res = await fetch(url, { cache: 'no-store' });
    if (!res.ok) throw new Error(`Could not fetch store data (HTTP ${res.status}). Make sure "${url}" is uploaded as a theme asset.`);

    const text = await res.text();
    if (!text.trim()) throw new Error('The CSV file is empty.');

    return StoreLocatorCSV.parse(text);
  }

  /* ── Map initialisation ───────────────────────────────── */

  async _initMap() {
    if (!this.dom.mapEl) return;

    // ── Guard: no API key configured ──────────────────────────
    // Skip loading entirely and show a clear placeholder so the
    // map area is not stuck on "Loading map…" indefinitely.
    if (!this.config.apiKey || !this.config.apiKey.trim()) {
      this._hideMapLoader(
        '🗺️ Add your API key in section settings to enable the map.',
        true /* isInfo */
      );
      return;
    }

    // Select provider
    if (this.config.mapProvider === 'mapbox') {
      this.mapProvider = new MapboxProvider(this.dom.mapEl, this.config);
    } else {
      this.mapProvider = new GoogleMapsProvider(this.dom.mapEl, this.config);
    }

    try {
      await this.mapProvider.load();
      this.mapReady = true;

      // Plot all stores on map
      this.mapProvider.plotMarkers(this.filteredStores, (store) => {
        this._onMarkerClick(store);
      });

    } catch (err) {
      // Map failed (e.g. bad API key, network issue) — show graceful fallback
      console.error('[StoreLocator] Map failed to load:', err);
      this._hideMapLoader('⚠️ Map unavailable — check your API key in section settings.');
    } finally {
      // Hide spinner loader regardless of outcome
      if (this.dom.mapLoader) {
        this.dom.mapLoader.classList.add('is-hidden');
      }
    }
  }

  /* ── Filter chips ─────────────────────────────────────── */

  _buildFilterChips(stores) {
    if (!this.dom.filtersBar) return;

    // Collect unique store types
    const types = [...new Set(stores.map(s => s.store_type).filter(Boolean))].sort();

    // Clear existing chips (keep "All" as first)
    this.dom.filtersBar.innerHTML = '';

    const allBtn = this._createFilterBtn('All', 'all', true);
    this.dom.filtersBar.appendChild(allBtn);

    types.forEach(type => {
      this.dom.filtersBar.appendChild(this._createFilterBtn(type, type, false));
    });

    // Hide filter bar if no store_type data
    if (types.length === 0) {
      this.dom.filtersBar.style.display = 'none';
    }
  }

  _createFilterBtn(label, value, isActive) {
    const btn = document.createElement('button');
    btn.type      = 'button';
    btn.className = `store-locator__filter-btn${isActive ? ' is-active' : ''}`;
    btn.dataset.filter = value;
    btn.textContent    = label;
    return btn;
  }

  /* ── Event bindings ───────────────────────────────────── */

  _bindEvents() {
    // Search input — debounced CSV filter on typing
    if (this.dom.searchInput) {
      this.dom.searchInput.addEventListener('input', () => {
        clearTimeout(this._searchDebounce);
        this._searchDebounce = setTimeout(() => {
          this.searchQuery = this.dom.searchInput.value.trim().toLowerCase();
          this._applyFilters();
        }, 280);
      });

      // Press Enter → geocode the typed location, pan map to show 5-10 miles radius, sort list by distance
      this.dom.searchInput.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter') return;
        const query = this.dom.searchInput.value.trim();
        if (!query || !this.mapReady || this.config.mapProvider !== 'google') return;
        const geocoder = new google.maps.Geocoder();
        geocoder.geocode({ address: query }, (results, status) => {
          if (status !== 'OK' || !results[0]) return;
          const loc = results[0].geometry.location;
          const lat = loc.lat();
          const lng = loc.lng();
          // Save on orchestrator so future re-renders keep distances
          this._userLatLng = { lat, lng };
          this._distanceLabel = query; // Store the search query as the distance label
          if (this.mapProvider.setUserLocation) {
            this.mapProvider.setUserLocation(lat, lng);
          }
          // Sort and re-render — _renderList will auto-append distances with the search label
          const sorted = this._sortByDistance(this.filteredStores, lat, lng);
          this._renderList(sorted);
          // Fit map to show 50-mile radius around search location
          this._fitTo50MileRadius(lat, lng);
        });
      });
    }

    // Clear search button — reset query but keep distances if user location is still set
    if (this.dom.searchClear) {
      this.dom.searchClear.addEventListener('click', () => {
        this.dom.searchInput.value = '';
        this.searchQuery = '';
        this._applyFilters(); // _applyFilters re-sorts by distance if _userLatLng exists
        this.dom.searchInput.focus();
      });
    }

    // Filter chips (event delegation)
    if (this.dom.filtersBar) {
      this.dom.filtersBar.addEventListener('click', (e) => {
        const btn = e.target.closest('.store-locator__filter-btn');
        if (!btn) return;

        this.dom.filtersBar.querySelectorAll('.store-locator__filter-btn')
          .forEach(b => b.classList.remove('is-active'));
        btn.classList.add('is-active');

        this.activeFilter = btn.dataset.filter;
        this._applyFilters();
      });
    }

    // Geolocation button
    if (this.dom.geolocateBtn) {
      this.dom.geolocateBtn.addEventListener('click', () => {
        this._detectLocation(true); // true = user-initiated (show errors)
      });
    }

    // Mobile tab switching
    if (this.dom.mobileTabs) {
      this.dom.mobileTabs.forEach(tab => {
        tab.addEventListener('click', () => {
          this.dom.mobileTabs.forEach(t => t.classList.remove('is-active'));
          tab.classList.add('is-active');
          const targetTab = tab.dataset.tab;
          if (this.dom.body) this.dom.body.dataset.activeTab = targetTab;
          // Trigger map resize after becoming visible
          if (targetTab === 'map' && this.mapReady && this.mapProvider?.map) {
            setTimeout(() => {
              if (this.config.mapProvider === 'google') {
                google.maps.event.trigger(this.mapProvider.map, 'resize');
              } else {
                this.mapProvider.map.resize();
              }
            }, 50);
          }
        });
      });
    }
  }

  /* ── Filtering + search logic ─────────────────────────── */

  _applyFilters() {
    const query  = this.searchQuery;
    const filter = this.activeFilter;

    let stores = this.allStores.filter(store => {
      const matchesSearch = !query || store.searchIndex.includes(query);
      const matchesFilter = filter === 'all' || store.store_type === filter;
      return matchesSearch && matchesFilter;
    });

    // Keep distance sort if user location is known
    if (this._userLatLng) {
      stores = this._sortByDistance(stores, this._userLatLng.lat, this._userLatLng.lng);
    }

    this.filteredStores = stores;
    this._renderList(this.filteredStores);

    if (this.mapReady && this.mapProvider) {
      this.mapProvider.plotMarkers(this.filteredStores, (store) => {
        this._onMarkerClick(store);
      });
    }
  }

  /* ── Map loader helpers ───────────────────────────────── */

  /** Show ~5–10 mile radius around a center point. */
  _fitTo50MileRadius(centerLat, centerLng) {
    if (!this.mapReady || !this.mapProvider) return;

    // Zoom 13 in Google ≈ ~5–10 mile view, matches Mapbox zoom ~12.
    if (this.config.mapProvider === 'mapbox' && this.mapProvider.map) {
      this.mapProvider.map.flyTo({ center: [centerLng, centerLat], zoom: 12 });
    } else if (this.mapProvider.map) {
      this.mapProvider.map.panTo({ lat: centerLat, lng: centerLng });
      this.mapProvider.map.setZoom(13);
    }
  }

  /**
   * Replace the map loading spinner with a static message.
   * Used when map cannot load (no key, API error, etc.).
   * @param {string}  message   — human-readable text to show
   * @param {boolean} isInfo    — true = grey info tone, false = amber warning tone
   */
  _hideMapLoader(message, isInfo = false) {
    // Hide the spinner element itself
    if (this.dom.mapLoader) {
      this.dom.mapLoader.classList.add('is-hidden');
    }
    // Replace map canvas with a friendly placeholder
    if (this.dom.mapEl) {
      const iconColor = isInfo ? 'rgba(var(--color-foreground),0.35)' : 'rgba(217,119,6,0.9)';
      this.dom.mapEl.innerHTML = `
        <div style="
          display:flex;align-items:center;justify-content:center;
          height:100%;min-height:inherit;padding:3rem 2rem;
          text-align:center;
          background:rgba(var(--color-foreground),0.02);
        ">
          <div>
            <div style="font-size:3.6rem;margin-bottom:1.2rem;line-height:1;">🗺️</div>
            <p style="
              margin:0;
              font-family:inherit;
              font-size:1.5rem;
              font-weight:600;
              color:${iconColor};
              line-height:1.4;
              max-width:28rem;
            ">${message}</p>
          </div>
        </div>`;
    }
  }

  /**
   * Update the list-count text directly (bypasses _renderList).
   * Used when an error prevents stores from loading.
   * @param {string} text
   */
  _setListCount(text) {
    if (this.dom.listCount) this.dom.listCount.textContent = text;
  }

  /* ── List rendering ───────────────────────────────────── */

  _renderList(stores) {
    if (!this.dom.listEl) return;

    // Update count
    if (this.dom.listCount) {
      const n = stores.length;
      this.dom.listCount.textContent = `${n} store${n !== 1 ? 's' : ''} found`;
    }

    // Empty state
    if (stores.length === 0) {
      this.dom.listEl.innerHTML = '';
      if (this.dom.emptyState) this.dom.emptyState.classList.add('is-visible');
      return;
    }

    if (this.dom.emptyState) this.dom.emptyState.classList.remove('is-visible');

    // DocumentFragment for performance
    const frag = document.createDocumentFragment();
    stores.forEach(store => frag.appendChild(this._buildCard(store)));
    this.dom.listEl.innerHTML = '';
    this.dom.listEl.appendChild(frag);

    // Re-append distances if user location is already known
    if (this._userLatLng) {
      this._appendDistances(stores, this._userLatLng.lat, this._userLatLng.lng, this._distanceLabel);
    }
  }

  _buildCard(store) {
    const card = document.createElement('div');
    card.className       = 'store-locator__card';
    card.dataset.storeId = store.id;
    card.setAttribute('role', 'button');
    card.setAttribute('tabindex', '0');
    card.setAttribute('aria-label', `View ${store.name} on map`);

    const badge      = store.store_type ? `<span class="store-locator__badge store-locator__badge--${this._badgeModifier(store.store_type)}">${this._esc(store.store_type)}</span>` : '';
    const phoneHtml  = store.phone    ? `<span class="store-locator__card-phone"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 11.1 19.79 19.79 0 01.01 2.41 2 2 0 012 .24h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></svg>${this._esc(store.phone)}</span>` : '';
    const websiteHtml = store.website ? `<a href="${this._esc(store.website)}" target="_blank" rel="noopener noreferrer" onclick="event.stopPropagation()"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/></svg>${this._esc(store.website.replace(/^https?:\/\//, ''))}</a>` : '';
    const notesHtml   = store.notes ? `<p class="store-locator__card-notes">${this._esc(store.notes)}</p>` : '';

    card.innerHTML = `
      <div class="store-locator__card-header">
        <h3 class="store-locator__card-name">${this._esc(store.name)}</h3>
        ${badge}
      </div>
      <p class="store-locator__card-address">${this._esc(store.fullAddress)}</p>
      <div class="store-locator__card-meta">${phoneHtml}${websiteHtml}</div>
      ${notesHtml}`;

    // Click → open info window on map
    const activate = () => {
      this._activateCard(card, store);
      if (this.mapReady && this.mapProvider && store.hasCoords) {
        this.mapProvider.openInfoForStore(store);
        // On mobile: switch to map tab
        this._switchToMapTab();
      } else if (!store.hasCoords) {
        this._showNotice('warning', `"${store.name}" does not have map coordinates.`);
      }
    };

    card.addEventListener('click', activate);
    card.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') activate(); });

    return card;
  }

  _activateCard(activeCard, store) {
    this.dom.listEl.querySelectorAll('.store-locator__card').forEach(c => c.classList.remove('is-active'));
    activeCard.classList.add('is-active');
    // Scroll card into view within list panel
    activeCard.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  /** Called when a map marker is clicked — highlight the corresponding list card. */
  _onMarkerClick(store) {
    if (!this.dom.listEl) return;
    const card = this.dom.listEl.querySelector(`[data-store-id="${store.id}"]`);
    if (card) this._activateCard(card, store);
  }

  /* ── Geolocation ──────────────────────────────────────── */

  _detectLocation(userInitiated = false) {
    if (!('geolocation' in navigator)) {
      if (userInitiated) this._showNotice('warning', 'Geolocation is not supported by your browser.');
      return;
    }

    if (this.dom.geolocateBtn) {
      this.dom.geolocateBtn.classList.add('is-loading');
      this.dom.geolocateBtn.setAttribute('disabled', '');
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude: lat, longitude: lng } = position.coords;
        this._onLocationFound(lat, lng);
      },
      (err) => {
        if (this.dom.geolocateBtn) {
          this.dom.geolocateBtn.classList.remove('is-loading');
          this.dom.geolocateBtn.removeAttribute('disabled');
        }
        // IP-based fallback: centre map on user's country via a free API
        this._ipFallback();
        if (userInitiated) {
          let msg = 'Could not get your location.';
          if (err.code === 1) msg = 'Location access denied. Please allow location access in your browser.';
          if (err.code === 2) msg = 'Location unavailable. Please try again.';
          if (err.code === 3) msg = 'Location request timed out.';
          this._showNotice('warning', msg);
        }
      },
      { timeout: 8000, enableHighAccuracy: false }
    );
  }

  _onLocationFound(lat, lng) {
    if (this.dom.geolocateBtn) {
      this.dom.geolocateBtn.classList.remove('is-loading');
      this.dom.geolocateBtn.removeAttribute('disabled');
    }

    // Persist on the orchestrator so all future re-renders keep distances
    this._userLatLng = { lat, lng };
    this._distanceLabel = 'your location'; // Reset to default when using actual geolocation

    // Pan map to user location at zoom 10 (~5–10 mile radius)
    if (this.mapReady && this.mapProvider) {
      this.mapProvider.panTo(lat, lng, 10);
      // Place red user-location dot (Google Maps only)
      if (this.mapProvider.setUserLocation) {
        this.mapProvider.setUserLocation(lat, lng);
      }
    }
    // Sort stores by distance and re-render list (distances auto-appended inside _renderList)
    const sorted = this._sortByDistance(this.filteredStores, lat, lng);
    this._renderList(sorted);
  }

  /* ── Auto-geocoding ───────────────────────────────────── */

  /**
   * Resolve lat/lng for any store rows that lack coordinates by
   * geocoding their full address. Results are cached in localStorage
   * keyed by address so we only pay the geocoding cost once per store.
   */
  async _geocodeMissing() {
    if (!this.mapReady) return;

    const missing = this.allStores.filter(s => !s.hasCoords && s.fullAddress);
    if (missing.length === 0) return;

    const cache = this._loadGeocodeCache();
    let updated = 0;
    let failedStatus = null; // remember last failure reason for user-facing notice

    console.log(`[StoreLocator] Geocoding ${missing.length} addresses without coordinates…`);

    for (const store of missing) {
      const key = store.fullAddress.toLowerCase();
      let coords = cache[key];

      if (!coords) {
        const result = await this._geocodeOne(store.fullAddress);
        if (result.coords) {
          coords = result.coords;
          cache[key] = coords;
          // Small delay between requests to stay under provider rate limits
          await new Promise(r => setTimeout(r, 150));
        } else {
          failedStatus = result.status;
          console.warn(`[StoreLocator] Geocoding failed for "${store.name}" (${store.fullAddress}) — ${result.status}`);
        }
      }

      if (coords) {
        store.lat = coords.lat;
        store.lng = coords.lng;
        store.hasCoords = true;
        updated++;
      }
    }

    this._saveGeocodeCache(cache);

    console.log(`[StoreLocator] Geocoded ${updated}/${missing.length} addresses successfully.`);

    // Re-render list + re-plot markers (also re-fits map bounds) if any resolved
    if (updated > 0) this._applyFilters();

    // If everything failed, show an actionable notice instead of staying silent
    if (updated === 0 && failedStatus) {
      this._showGeocodeFailureNotice(failedStatus);
    }
  }

  /** Surface a clear, actionable message when geocoding can't run. */
  _showGeocodeFailureNotice(status) {
    let msg = `Could not auto-locate stores from addresses (${status}).`;
    if (status === 'REQUEST_DENIED') {
      msg = 'Auto-geocoding is blocked: enable the Geocoding API for your Google Maps key in Google Cloud Console (APIs & Services → Library → Geocoding API → Enable). Until then, add latitude/longitude in your CSV manually.';
    } else if (status === 'OVER_QUERY_LIMIT') {
      msg = 'Geocoding quota exceeded. Check Google Cloud billing or add latitude/longitude to your CSV manually.';
    } else if (status === 'ZERO_RESULTS') {
      msg = 'Some addresses could not be geocoded. Check that they are complete and correctly formatted.';
    } else if (status === 'NETWORK_ERROR') {
      msg = 'Network error while geocoding addresses. Check your connection or add latitude/longitude to your CSV manually.';
    }
    this._showNotice('warning', msg);
  }

  /** Geocode a single address using the configured map provider. */
  _geocodeOne(address) {
    if (this.config.mapProvider === 'mapbox') return this._geocodeMapbox(address);
    return this._geocodeGoogle(address);
  }

  _geocodeGoogle(address) {
    if (!window.google || !google.maps || !google.maps.Geocoder) {
      return Promise.resolve({ coords: null, status: 'SDK_NOT_LOADED' });
    }
    return new Promise(resolve => {
      new google.maps.Geocoder().geocode({ address }, (results, status) => {
        if (status === 'OK' && results && results[0]) {
          const loc = results[0].geometry.location;
          resolve({ coords: { lat: loc.lat(), lng: loc.lng() }, status });
        } else {
          resolve({ coords: null, status });
        }
      });
    });
  }

  async _geocodeMapbox(address) {
    try {
      const token = this.config.apiKey;
      const url   = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(address)}.json?access_token=${token}&limit=1`;
      const res   = await fetch(url);
      if (!res.ok) return { coords: null, status: `HTTP_${res.status}` };
      const data  = await res.json();
      const f     = data.features && data.features[0];
      if (f && f.center) return { coords: { lat: f.center[1], lng: f.center[0] }, status: 'OK' };
      return { coords: null, status: 'ZERO_RESULTS' };
    } catch (e) {
      return { coords: null, status: 'NETWORK_ERROR' };
    }
  }

  _loadGeocodeCache() {
    try { return JSON.parse(localStorage.getItem('sl_geocode_cache') || '{}'); }
    catch { return {}; }
  }

  _saveGeocodeCache(cache) {
    try { localStorage.setItem('sl_geocode_cache', JSON.stringify(cache)); }
    catch (_) { /* quota exceeded — ignore */ }
  }

  /** Fallback: use ipapi.co (free, no key) to get rough country coordinates. */
  async _ipFallback() {
    try {
      const res  = await fetch('https://ipapi.co/json/', { signal: AbortSignal.timeout(4000) });
      const data = await res.json();
      if (data.latitude && data.longitude && this.mapReady && this.mapProvider) {
        this.mapProvider.panTo(data.latitude, data.longitude, 6);
      }
    } catch (_) { /* silent fail — map stays on default view */ }
  }

  _sortByDistance(stores, lat, lng) {
    return [...stores].sort((a, b) => {
      if (!a.hasCoords && !b.hasCoords) return 0;
      if (!a.hasCoords) return 1;
      if (!b.hasCoords) return -1;
      return this._haversine(lat, lng, a.lat, a.lng)
           - this._haversine(lat, lng, b.lat, b.lng);
    });
  }

  /** Append distance text to each card after geolocation. */
  _appendDistances(sortedStores, userLat, userLng, label = 'your location') {
    sortedStores.forEach(store => {
      if (!store.hasCoords) return;
      const distKm = this._haversine(userLat, userLng, store.lat, store.lng);
      const distMi = distKm * 0.621371;
      const card   = this.dom.listEl?.querySelector(`[data-store-id="${store.id}"]`);
      if (!card) return;

      // Remove existing distance badge
      card.querySelector('.store-locator__card-distance')?.remove();

      const el = document.createElement('p');
      el.className = 'store-locator__card-distance';
      el.innerHTML = `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>${distMi.toFixed(1)} mi (${distKm.toFixed(1)} km) from ${this._esc(label)}`;
      card.appendChild(el);
    });
  }

  /** Haversine great-circle distance in km. */
  _haversine(lat1, lng1, lat2, lng2) {
    const R    = 6371;
    const dLat = this._toRad(lat2 - lat1);
    const dLng = this._toRad(lng2 - lng1);
    const a    = Math.sin(dLat / 2) ** 2
               + Math.cos(this._toRad(lat1)) * Math.cos(this._toRad(lat2)) * Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  _toRad(deg) { return deg * (Math.PI / 180); }

  /* ── Mobile tab helper ────────────────────────────────── */

  _switchToMapTab() {
    if (window.innerWidth >= 990) return;
    if (this.dom.body) this.dom.body.dataset.activeTab = 'map';
    this.dom.mobileTabs.forEach(t => {
      t.classList.toggle('is-active', t.dataset.tab === 'map');
    });
    if (this.mapProvider?.map) {
      setTimeout(() => {
        if (this.config.mapProvider === 'google') {
          google.maps.event.trigger(this.mapProvider.map, 'resize');
        } else {
          this.mapProvider.map.resize?.();
        }
      }, 50);
    }
  }

  /* ── Notice banner ────────────────────────────────────── */

  _showNotice(type, message) {
    if (!this.dom.noticeBanner || !this.dom.noticeText) return;
    this.dom.noticeBanner.classList.remove('store-locator__notice--error', 'store-locator__notice--warning');
    this.dom.noticeBanner.classList.add(`store-locator__notice--${type}`, 'is-visible');
    this.dom.noticeText.textContent = message;
    // Auto-dismiss warnings after 8s
    if (type === 'warning') {
      setTimeout(() => this.dom.noticeBanner.classList.remove('is-visible'), 8000);
    }
  }

  /* ── Helpers ──────────────────────────────────────────── */

  _badgeModifier(type) {
    const t = (type || '').toLowerCase();
    if (t.includes('shop'))        return 'shop';
    if (t.includes('distributor')) return 'distributor';
    if (t.includes('demo'))        return 'demo';
    return 'default';
  }

  _esc(str) {
    return String(str || '')
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }
}


/* ================================================================
   5. AUTO-INITIALISATION
   ================================================================ */

/**
 * The Liquid section embeds a <script type="application/json"> tag
 * with id="store-locator-config-{sectionId}" containing the config.
 * This block finds all such configs on the page and bootstraps each.
 */
(function () {
  function boot() {
    document.querySelectorAll('script[id^="store-locator-config-"]').forEach(script => {
      try {
        const config = JSON.parse(script.textContent);
        // Only init once (guard against HMR / double execution)
        const rootEl = document.getElementById(`store-locator-${config.sectionId}`);
        if (!rootEl || rootEl.dataset.slInitialised) return;
        rootEl.dataset.slInitialised = 'true';
        new StoreLocator(config);
      } catch (e) {
        console.error('[StoreLocator] Failed to parse config:', e);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
